import { Component } from '@theme/component';

/**
 * Header navigation for a native resource-list carousel (`.resource-list__track`).
 *
 * The arrows live in the section header, a sibling of the carousel, so they can't
 * use scoped controls. This pages the track with `scrollBy` and mirrors its
 * start/end disabled state. Hides itself when the track isn't scrollable.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CarouselNav extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @returns {HTMLElement | null} The carousel's scroll track within this section. */
  get track() {
    const root = this.closest('.section-resource-list, .section-carousel') ?? document;
    return root.querySelector('.resource-list__carousel .resource-list__track');
  }

  connectedCallback() {
    super.connectedCallback();

    const { track } = this;

    if (!track) {
      this.hidden = true;
      return;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    track.addEventListener('scroll', this.#sync, { passive: true, signal });
    window.addEventListener('resize', this.#sync, { signal });

    requestAnimationFrame(this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  previous() {
    this.#page(-1);
  }

  next() {
    this.#page(1);
  }

  /** @param {number} direction -1 for previous, 1 for next. */
  #page(direction) {
    const { track } = this;
    if (!track) return;

    const slide = track.querySelector('.resource-list__slide');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor(track.clientWidth / step));

    track.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
  }

  #sync = () => {
    const { track } = this;
    if (!track) return;

    const { previous, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);

    this.hidden = max <= 1;

    if (previous) previous.disabled = position <= 1;
    if (next) next.disabled = position >= max - 1;
  };
}

if (!customElements.get('carousel-nav-component')) {
  customElements.define('carousel-nav-component', CarouselNav);
}
